import React, { ReactNode, useMemo } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { DeleteProps } from '@fabric-ui/components/Table';
import { Delete, Edit, Plus } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { RowSelectionState } from '@tanstack/react-table';
// import { Link } from '../../../../../client/src/utils/AvRouter';
import { DeleteButtonWithDialog, ToolbarActionButton } from '../Table';
import { DataRecord } from './table.types';

const plusIcon = <Plus style={{ marginLeft: '-6px' }} />;
const deleteIcon = <Delete style={iconSize()} />;
const editIcon = <Edit style={iconSize()} />;

const NO_PERMISSION_MESSAGE = 'You need permissions to perform this action';

type NavLinkLikeProps = {
  to: string | { to: string; search?: any; state?: any };
  children?: React.ReactNode;
  className?: string;
};
export interface TableToolbarV2BaseProps {
  keyName: string;
  rows: DataRecord[];
  rowSelection: RowSelectionState;
  onRowSelectionChange?: (newRowSelection: RowSelectionState, rows: DataRecord[]) => void;
  deleteProps?: DeleteProps;
  customActions?: ((
    selectedIds: string[],
    selectedRows: DataRecord[],
    onRowSelectionChange: (newRowSelection: RowSelectionState, rows: DataRecord[]) => void
  ) => ReactNode)[];
  customGlobalActions?: (() => ReactNode)[];
  allowEdit?: boolean;
  allowCreate?: boolean;
  customNewButton?: ReactNode;
  innerEditLink?: (v: any) => string | { to: string; search?: any; state?: any };
  // @TODO CHANGE BEFORE MERGE
  Link: React.ElementType<NavLinkLikeProps>;
}

export const TableToolbarV2Base: React.FC<TableToolbarV2BaseProps> = ({
  keyName,
  rows,
  rowSelection,
  onRowSelectionChange,
  deleteProps,
  customActions = [],
  customGlobalActions = [],
  allowEdit = false,
  allowCreate = false,
  customNewButton,
  innerEditLink,
  Link,
}) => {
  const selectedIds = useMemo(() => Object.keys(rowSelection).filter(Boolean), [rowSelection]);
  const selectedCount = selectedIds.length;

  const selectedRows = useMemo(() => {
    if (!selectedCount) return [];
    const selection = new Set(selectedIds);
    return rows.filter(row => selection.has(`${row[keyName]}`));
  }, [rows, selectedIds, keyName, selectedCount]);

  const getSelectedRowName = (selectedId: string) => {
    const row = rows.find(r => `${r[keyName]}` === selectedId);
    return deleteProps?.getName?.(row);
  };

  const handleSelected = (newRowSelection: RowSelectionState) => {
    onRowSelectionChange?.(newRowSelection, rows);
  };

  const message =
    rows.length && deleteProps?.customMessage
      ? deleteProps.customMessage(selectedRows)
      : deleteProps?.getName
        ? selectedIds.map(selectedId => getSelectedRowName(selectedId)).join(', ')
        : '';

  const deleteButtonFunc = (onClick: () => void) =>
    ToolbarActionButton(
      `Delete${selectedCount > 1 ? ` (${selectedCount})` : ''}`,
      onClick,
      deleteIcon,
      selectedCount === 0 || !deleteProps?.onDelete || (deleteProps?.disableMultipleDelete && selectedCount > 1)
    );

  return (
    <Box sx={{ ...flex.row }}>
      {customActions.map(action => (
        <Box key={action.name}>{action(selectedIds, selectedRows, handleSelected)}</Box>
      ))}
      {deleteProps && (
        <DeleteButtonWithDialog
          button={deleteButtonFunc}
          selected={selectedIds}
          handleSelected={() => {
            handleSelected?.({});
          }}
          deleteProps={{ ...deleteProps, message }}
        />
      )}
      {allowEdit && (
        <div>
          <Button
            component={Link}
            variant="filter"
            className="transparent"
            to={innerEditLink?.(selectedIds[0]) || `edit/${selectedIds[0]}`}
            disabled={selectedCount !== 1}>
            {editIcon}
            <span>Edit</span>
          </Button>
        </div>
      )}
      <span style={{ flexGrow: 1 }} />
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        {customNewButton ?? (
          <AvTooltip disabled={allowCreate} title={NO_PERMISSION_MESSAGE}>
            <div>
              <Button
                disabled={!allowCreate}
                onClick={e => e.stopPropagation()}
                component={Link}
                to="create/"
                variant="contained"
                size="small">
                {plusIcon}
                <span>Create</span>
              </Button>
            </div>
          </AvTooltip>
        )}
        {customGlobalActions.map(action => (
          <React.Fragment key={action.name}>{action()}</React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
